import centroAutoTransferConfig from './centro-auto-transfer-config.json';

const CENTRO_AUTO_TRANSFER_CONFIG_URL =
  'https://moneytor-uploads.s3.ap-south-1.amazonaws.com/developer/portfolio-zestmoneyJSONData/centro-auto-transfer-config.json';

export interface CentroCampaignTransferRule {
  dummyUsers: string[];
  targetCampaign: string;
  activeUsers: string[];
}

export interface CentroAutoTransferConfig {
  campaigns: Record<string, CentroCampaignTransferRule>;
}

export interface CentroAutoTransferProfile {
  enabled: boolean;
  targetAgentId: string;
  targetCampaign: string;
  loginCampaign: string;
}

interface StoredCampaignRule {
  campaignName: string;
  rule: CentroCampaignTransferRule;
}

const campaignRules = new Map<string, StoredCampaignRule>();

function normalizeId(value: string): string {
  return String(value ?? '').trim().toLowerCase();
}

function normalizeCampaignKey(value: string): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  return raw.split('~')[0].trim().toLowerCase();
}

function normalizeUserList(value: unknown): string[] {
  if (value == null) return [];
  const items = Array.isArray(value) ? value : [value];
  const out: string[] = [];
  for (const item of items) {
    const raw = String(item ?? '').trim();
    if (!raw) continue;
    const parts = raw.includes(',') ? raw.split(',') : [raw];
    for (const part of parts) {
      const id = String(part ?? '').trim();
      if (id && !out.some((existing) => normalizeId(existing) === normalizeId(id))) {
        out.push(id);
      }
    }
  }
  return out;
}

function resolveCampaignRule(campaignName: string): StoredCampaignRule | null {
  const key = normalizeCampaignKey(campaignName);
  if (!key) return null;
  return campaignRules.get(key) || null;
}

function applyConfig(raw: unknown): void {
  campaignRules.clear();
  const config = raw as Partial<CentroAutoTransferConfig> | null | undefined;
  const campaigns = config?.campaigns;
  if (!campaigns || typeof campaigns !== 'object' || Array.isArray(campaigns)) return;
  for (const [campaignName, entry] of Object.entries(campaigns)) {
    const name = String(campaignName ?? '').trim();
    if (!name) continue;
    const rule: CentroCampaignTransferRule = {
      dummyUsers: normalizeUserList(entry?.dummyUsers),
      targetCampaign: String(entry?.targetCampaign ?? '').trim(),
      activeUsers: normalizeUserList(entry?.activeUsers),
    };
    if (!rule.targetCampaign && rule.activeUsers.length === 0 && rule.dummyUsers.length === 0) continue;
    campaignRules.set(normalizeCampaignKey(name), { campaignName: name, rule });
  }
}

applyConfig(centroAutoTransferConfig);

void fetch(CENTRO_AUTO_TRANSFER_CONFIG_URL)
  .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
  .then(applyConfig)
  .catch(() => {});

export function applyCentroAutoTransferConfig(raw: unknown): void {
  applyConfig(raw);
}

export function getCentroAutoTransferCampaigns(): readonly string[] {
  return Array.from(campaignRules.values()).map((entry) => entry.campaignName);
}

export function isCentroAutoTransferConfigReady(): boolean {
  for (const { rule } of campaignRules.values()) {
    if (rule.targetCampaign && rule.activeUsers.length > 0) return true;
  }
  return false;
}

export function isCentroAutoTransferSourceCampaign(campaignName: string): boolean {
  return !!resolveCampaignRule(campaignName);
}

export function getCentroAutoTransferTargetCampaign(campaignName: string): string {
  return resolveCampaignRule(campaignName)?.rule.targetCampaign || '';
}

export function isCentroAutoTransferDummyAgent(agentId: string, campaignName: string): boolean {
  const needle = normalizeId(agentId);
  if (!needle) return false;
  const stored = resolveCampaignRule(campaignName);
  if (!stored || stored.rule.dummyUsers.length === 0) return true;
  return stored.rule.dummyUsers.some((id) => normalizeId(id) === needle);
}

export function pickCentroAutoTransferTarget(
  onlineAgentIds: string[] | undefined,
  campaignName: string
): string | null {
  const stored = resolveCampaignRule(campaignName);
  if (!stored) return null;
  const active = stored.rule.activeUsers.map((id) => String(id ?? '').trim()).filter(Boolean);
  if (!active.length) return null;
  const onlineSet = new Set((onlineAgentIds || []).map((id) => normalizeId(id)).filter(Boolean));
  const onlineActive = onlineSet.size > 0 ? active.filter((id) => onlineSet.has(normalizeId(id))) : [];
  const candidates = onlineActive.length > 0 ? onlineActive : active;
  return candidates[Math.floor(Math.random() * candidates.length)] || null;
}

const emptyProfile: CentroAutoTransferProfile = {
  enabled: false,
  targetAgentId: '',
  targetCampaign: '',
  loginCampaign: '',
};

export function resolveCentroAutoTransferProfile(
  agentId: string,
  loginCampaign: string,
  onlineAgentIds?: string[]
): CentroAutoTransferProfile {
  const campaignKey = normalizeCampaignKey(loginCampaign);
  if (!campaignKey || !isCentroAutoTransferConfigReady()) return emptyProfile;
  const stored = resolveCampaignRule(loginCampaign);
  if (!stored) return emptyProfile;
  const agent = String(agentId ?? '').trim();
  if (!agent || !isCentroAutoTransferDummyAgent(agent, stored.campaignName)) return emptyProfile;
  const targetCampaign = stored.rule.targetCampaign;
  const targetAgentId = pickCentroAutoTransferTarget(onlineAgentIds, stored.campaignName) || '';
  return {
    enabled: !!targetCampaign && !!targetAgentId,
    targetAgentId,
    targetCampaign,
    loginCampaign: stored.campaignName,
  };
}
